var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Literal, NamedNode } from 'lincd/models';
import { linkedShape } from './package.js';
import { rdfs } from './ontologies/rdfs.js';
import { rdf } from 'lincd/ontologies/rdf';
import { Prefix } from 'lincd/utils/Prefix';
import { Shape } from 'lincd/shapes/Shape';
import { createPropertyShape, literalProperty, } from 'lincd/shapes/SHACL';
import { shacl } from 'lincd/ontologies/shacl';
let Resource = class Resource extends Shape {
    get type() {
        return this.getOneAs(rdf.type, Class);
    }
    set type(val) {
        this.overwrite(rdf.type, val.namedNode);
    }
};
Resource.targetClass = rdfs.Resource;
Resource = __decorate([
    linkedShape
], Resource);
export { Resource };
let Class = class Class extends Resource {
    get isDefinedBy() {
        return this.getOne(rdfs.isDefinedBy);
    }
    set isDefinedBy(value) {
        this.overwrite(rdfs.isDefinedBy, value);
    }
    /**
     * Gets the ontology node that defines this property
     * Uses isDefinedBy but also looks at the URI for known matches with prefixes as a fallback
     */
    get ontology() {
        let isDefinedBy = this.isDefinedBy;
        if (isDefinedBy) {
            return isDefinedBy;
        }
        let [ontologyURI] = Prefix.findMatch(this.namedNode.uri);
        if (ontologyURI) {
            return NamedNode.getOrCreate(ontologyURI);
        }
    }
    getNumLocalInstances(explicitInstancesOnly = false, filterLocalNodes = false) {
        //since we want to use the same filters, and count unique subjects of quads
        //there is no faster way to count the total number without actually building the result set
        //so here we just return the size of the number of local instances with the same filters (and no limit or offset)
        return this.getLocalInstanceResources(explicitInstancesOnly, filterLocalNodes).size;
    }
    getLocalInstanceResources(explicitInstancesOnly = false, filterLocalNodes = false, limit = NaN, offset = 0) {
        let result;
        if (explicitInstancesOnly) {
            if (filterLocalNodes) {
                result = this.node
                    .getInverseQuads(rdf.type)
                    .filter((quad) => !quad.implicit && !quad.subject.isTemporaryNode)
                    .getSubjects();
            }
            else {
                result = this.node
                    .getInverseQuads(rdf.type)
                    .filter((quad) => !quad.implicit)
                    .getSubjects();
            }
        }
        else {
            if (filterLocalNodes) {
                result = this.node
                    .getAllInverse(rdf.type)
                    .filter((n) => !n.isTemporaryNode);
            }
            else {
                result = this.node.getAllInverse(rdf.type);
            }
        }
        if (!isNaN(limit)) {
            let start = offset;
            let end = offset + limit;
            result = result.slice(start, end);
        }
        return result;
    }
};
Class.targetClass = rdfs.Class;
Class = __decorate([
    linkedShape
], Class);
export { Class };
let RdfsLiteral = class RdfsLiteral extends Shape {
    constructor(node) {
        super(node);
    }
    get node() {
        return this._node;
    }
    get value() {
        return this.node.value;
    }
    get uriResource() {
        return null;
    }
};
RdfsLiteral.targetClass = rdfs.Literal;
RdfsLiteral.preferredNodeKind = Literal;
RdfsLiteral = __decorate([
    linkedShape,
    __metadata("design:paramtypes", [Literal])
], RdfsLiteral);
export { RdfsLiteral };
let Property = class Property extends Resource {
    get comment() {
        return this.getOne(rdfs.comment).value;
    }
    set comment(value) {
        this.overwrite(rdfs.comment, new Literal(value));
    }
    get domain() {
        return this.getOneAs(rdfs.domain, Class);
    }
    set domain(value) {
        this.overwrite(rdfs.domain, value.namedNode);
    }
    get isDefinedBy() {
        return this.getOne(rdfs.isDefinedBy);
    }
    set isDefinedBy(value) {
        this.overwrite(rdfs.isDefinedBy, value);
    }
    /**
     * Gets the ontology node that defines this property
     * Uses isDefinedBy but also looks at the URI for known matches with prefixes as a fallback
     */
    get ontology() {
        let isDefinedBy = this.isDefinedBy;
        if (isDefinedBy) {
            return isDefinedBy;
        }
        let [ontologyURI] = Prefix.findMatch(this.namedNode.uri);
        if (ontologyURI) {
            return NamedNode.getOrCreate(ontologyURI);
        }
    }
    set ontology(value) {
        this.isDefinedBy = value;
    }
    get range() {
        return this.getOneAs(rdfs.range, Class);
    }
    set range(value) {
        this.overwrite(rdfs.range, value.namedNode);
    }
};
// static type: NamedNode = rdf.Property;
Property.targetClass = rdf.Property;
__decorate([
    literalProperty({
        path: rdfs.comment,
        maxCount: 1,
    }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], Property.prototype, "comment", null);
Property = __decorate([
    linkedShape
], Property);
export { Property };
createPropertyShape({
    path: rdf.type,
    shape: Class,
}, 'type', shacl.IRI, Resource);
createPropertyShape({
    path: rdf.type,
    shape: Class,
}, 'type', shacl.IRI, Resource);
createPropertyShape({
    path: rdfs.domain,
    shape: Class,
}, 'domain', shacl.IRI, Property);
createPropertyShape({
    path: rdfs.range,
    shape: Class,
}, 'range', shacl.IRI, Property);
//# sourceMappingURL=shapes.js.map